/* Per-user column freeze for Information tables (type 14) — SS05971.
   Rationale and decision log: docs/plans/SS05971-freeze-columns-plan.md.

   Pure logic: no Vue, no DOM. All widths are injected by the caller.

   Every frozen column needs a fixed pixel width, because each one's sticky `left` is the
   running sum of the widths before it. That width is the column's measured width, so pinning
   changes nothing on screen (AC23, D27); the type table below is only the unmeasured fallback.
   Selection is an arbitrary set, not a contiguous prefix (D9). */

/* fts-components.scss — @mixin table-row-action-width */
const ROW_ACTION_WIDTH = 51;
/* fts-components.scss — .te-signature-display / .table-display-signature */
const SIGNATURE_WIDTH = 300;
/* A frozen block wider than this share of the visible width leaves too little to scroll */
const MAX_FROZEN_RATIO = 0.7;
/* Ceiling for a frozen column (D28). Just above the widest type default (Content, 395), so it
   engages only on an Auto column stretched by long content. */
const MAX_FROZEN_COLUMN_WIDTH = 400;
/* At or below this the pin floats over the label instead of sharing its flex row (D30) —
   narrower than this the row overflows and carries the pin outside its own header cell.
   Measured in Chromium against the header's 0.875rem font; re-measure if that or the cell
   padding changes. */
const PIN_OVERLAY_MAX_WIDTH = 76;

/* Unmeasured fallback only. The two table components disagree on their per-type defaults, so a
   frozen column takes the wider of the two — never narrower than either mode already shows, and
   view and edit then compute identical offsets. */
const FrozenWidth = Object.freeze({
  Display: 150,   /* edit 35, display 150 */
  YesNo: 50,      /* edit 50, display 50 */
  Attachment: 250,/* edit 250, display 250 */
  Content: 395,   /* edit 395, display 150 */
  Phone: 200,     /* edit 200, display 150 */
  Signature: SIGNATURE_WIDTH,
  Default: 150
});

/* `==` is deliberate: `type` arrives as a number or a numeric string. */
const isType = (item, ...ids) => ids.some(id => item.type == id);
const isInput = (item, ...inputs) => inputs.some(input => item.input === input);

/* Unmeasured fallback width in px. A configured columnWidth wins outright. */
const resolveFrozenColumnWidth = (item) => {
  if (!item) return FrozenWidth.Default;

  const custom = parseInt(item.columnWidth, 10);
  if (Number.isFinite(custom) && custom > 0) return custom;

  /* Predicate order must match getColumnStyle in FTSTableEdit — a readOnly signature column
     resolves as Display there. */
  if (item.display || item.input === 'display' || item.readOnly) return FrozenWidth.Display;
  if (isType(item, 1) || isInput(item, 'boolean')) return FrozenWidth.YesNo;
  if (isType(item, 10, 11, 12, 13) ||
      isInput(item, 'imageUpload', 'imagesUpload', 'fileUpload', 'filesUpload'))
    return FrozenWidth.Attachment;
  if (isType(item, 22) || isInput(item, 'content')) return FrozenWidth.Content;
  if (isType(item, 26) || isInput(item, 'phoneNumber')) return FrozenWidth.Phone;
  if (isType(item, 29) || isInput(item, 'signature')) return FrozenWidth.Signature;
  return FrozenWidth.Default;
}

/* Types whose content reflows when the column narrows. Exempt from MAX_FROZEN_COLUMN_WIDTH
   (D28): clamping them re-wraps their content and grows the row, which AC14 forbids. Too-wide
   exempt columns are still caught by freezeEligibility's block budget. */
const isReflowing = (item) =>
  Boolean(item) && (
    isType(item, 8, 16, 21, 22, 24, 28) ||
    isInput(item, 'modules', 'multiline-text', 'users', 'content', 'context', 'tag')
  );

/* The width to pin a frozen column at → { width, clamped }.
   Deliberately NOT rounded: `left` is the running sum of these, so rounding each independently
   drifts the sum off the real cell positions.
   `clamped` tells the caller to clip that cell — otherwise a value that fitted one line at its
   natural width re-wraps at 400px and takes the row height with it (AC14). */
const pinnedWidth = (item, measured) => {
  const natural = (Number.isFinite(measured) && measured > 0)
    ? measured
    : resolveFrozenColumnWidth(item);

  if (isReflowing(item) || natural <= MAX_FROZEN_COLUMN_WIDTH) {
    return { width: natural, clamped: false };
  }
  return { width: MAX_FROZEN_COLUMN_WIDTH, clamped: true };
}

/* pinnedWidth without the `clamped` flag, for callers that only need the number. */
const columnWidthFor = (item, measured) => pinnedWidth(item, measured).width;

/* Look one column's measured width out of the caller's snapshot. Keyed by fieldName for
   the same reason the stored preference is (D8): indices move when the design changes. */
const measuredWidthOf = (item, measuredWidths) =>
  (item && measuredWidths) ? measuredWidths[item.fieldName] : undefined;

/* Stored fieldNames (D8) → frozen indices, ascending. A name that no longer exists is dropped
   (AC7). */
const resolveFrozenIndices = (fields, storedFieldNames) => {
  if (!Array.isArray(fields) || !Array.isArray(storedFieldNames) || !storedFieldNames.length) {
    return [];
  }

  const indices = [];
  fields.forEach((field, index) => {
    if (field && storedFieldNames.indexOf(field.fieldName) > -1) indices.push(index);
  });

  return indices;
}

/* What the indicator chip lists (D35): [{ index, fieldName, label }] in render order.
   `labelOf` is injected because the two table components resolve a header label differently —
   hardcoding either rule here would duplicate it and then drift (F2). */
const frozenSummary = (fields, frozenIndices, labelOf) => {
  if (!Array.isArray(fields) || !Array.isArray(frozenIndices)) return [];

  const resolve = typeof labelOf === 'function' ? labelOf : null;

  return frozenIndices
    .slice()
    .sort((a, b) => a - b)
    .map(index => {
      const field = fields[index];
      if (!field) return null;

      const label = resolve ? resolve(field) : null;
      return {
        index: index,
        fieldName: field.fieldName,
        /* a column with no label at all still has to be nameable in the list */
        label: (label === null || label === undefined || label === '')
          ? field.fieldName
          : label
      };
    })
    .filter(Boolean);
}

/* The stored fieldNames after toggling `index`, in render order (cosmetic — order does not
   affect rendering). */
const toggleFrozenFieldName = (fields, storedFieldNames, index) => {
  if (!Array.isArray(fields)) return [];

  const field = fields[index];
  if (!field || !field.fieldName) return Array.isArray(storedFieldNames) ? storedFieldNames : [];

  const current = Array.isArray(storedFieldNames) ? storedFieldNames : [];
  const next = current.indexOf(field.fieldName) > -1
    ? current.filter(name => name !== field.fieldName)
    : current.concat(field.fieldName);

  return fields
    .map(x => x && x.fieldName)
    .filter(name => name && next.indexOf(name) > -1);
}

/* Per-column geometry aligned to `fields` by index: { left, width, isLast, clamped } where
   frozen, null elsewhere. `measuredWidths` is fieldName → rendered px from the caller's
   pre-toggle pass; absent entries fall back to the type table.
   `left` is relative to the scrollport's left edge, so the block pins flush to it — the same
   edge the sticky header pins to. */
const buildFrozenColumns = (fields, frozenIndices, options) => {
  const {
    hasRowAction = false,
    measuredWidths = null,
    rowActionWidth = ROW_ACTION_WIDTH
  } = options || {};
  if (!Array.isArray(fields)) return [];

  const frozen = Array.isArray(frozenIndices) ? frozenIndices : [];
  const lastIndex = frozen.length ? Math.max.apply(null, frozen) : -1;
  let left = frozen.length && hasRowAction ? rowActionWidth : 0;

  return fields.map((field, index) => {
    if (frozen.indexOf(index) === -1) return null;

    const pinned = pinnedWidth(field, measuredWidthOf(field, measuredWidths));
    const entry = {
      left: left,
      width: pinned.width,
      isLast: index === lastIndex,
      clamped: pinned.clamped
    };
    left += pinned.width;
    return entry;
  });
}

/* Total px of the frozen block, row-action column included — what the eligibility rule compares
   against the visible width. Same options as buildFrozenColumns. */
const frozenBlockWidth = (fields, frozenIndices, options) => {
  const {
    hasRowAction = false,
    measuredWidths = null,
    rowActionWidth = ROW_ACTION_WIDTH
  } = options || {};
  if (!Array.isArray(fields)) return 0;

  const frozen = Array.isArray(frozenIndices) ? frozenIndices : [];
  if (!frozen.length) return 0;

  const columns = frozen.reduce((total, index) => {
    const field = fields[index];
    return total + columnWidthFor(field, measuredWidthOf(field, measuredWidths));
  }, 0);

  return columns + (hasRowAction ? rowActionWidth : 0);
}

/* What the header actually renders for a column, or 0 when unknowable (caller then fails open).
   Deliberately does NOT fall back to the type table, unlike every other width resolution here:
   its YesNo default of 50px is narrower than any YesNo column really renders, so a width rule
   applied to it would act on a width that does not exist. */
const renderedWidthOf = (item, measured) => {
  if (Number.isFinite(measured) && measured > 0) return measured;

  const configured = parseInt(item && item.columnWidth, 10);
  return (Number.isFinite(configured) && configured > 0) ? configured : 0;
}

/* Whether this column's pin must float over the label rather than sit beside it. Lives here so
   the two components cannot disagree about where a pin goes. Unknown width → false, keeping the
   pin in flow, which is correct for every width above the switch. `maxWidth` is for the tests. */
const pinOverlayRequired = (item, measured, maxWidth) => {
  const limit = Number.isFinite(maxWidth) ? maxWidth : PIN_OVERLAY_MAX_WIDTH;
  const width = renderedWidthOf(item, measured);

  return width > 0 && width <= limit;
}

/* Whether the user may additionally freeze `index` → { eligible, blockWidth, limit }.
   The block budget is the ONLY thing that refuses a freeze: columnWidth is unbounded in the
   designer, so a wide column can leave nothing to scroll.
   Narrowness deliberately does not refuse (D30) — the pin stays reachable at every width, and a
   40px key column is a good freeze. Do not reintroduce a minimum-width rule here.
   Unknown container width never blocks; an already-frozen column is always eligible, so a freeze
   can always be undone.
   Resolves widths exactly as the geometry does (D29) — the tooltip quotes these numbers, so they
   must describe the block the table really renders. */
const freezeEligibility = (fields, index, options) => {
  const {
    frozenIndices = [],
    hasRowAction = false,
    measuredWidths = null,
    rowActionWidth = ROW_ACTION_WIDTH,
    containerWidth = 0,
    maxRatio = MAX_FROZEN_RATIO
  } = options || {};

  const frozen = Array.isArray(frozenIndices) ? frozenIndices : [];
  /* the same geometry the pinning path uses, so the two can never disagree about a width */
  const blockOptions = {
    hasRowAction: hasRowAction,
    measuredWidths: measuredWidths,
    rowActionWidth: rowActionWidth
  };

  /* unfreezing always allowed */
  if (frozen.indexOf(index) > -1) {
    return {
      eligible: true,
      blockWidth: frozenBlockWidth(fields, frozen, blockOptions),
      limit: containerWidth > 0 ? containerWidth * maxRatio : 0
    };
  }

  const candidate = frozen.concat(index);
  const blockWidth = frozenBlockWidth(fields, candidate, blockOptions);
  const limit = containerWidth > 0 ? containerWidth * maxRatio : 0;

  if (!containerWidth || containerWidth <= 0) {
    return { eligible: true, blockWidth: blockWidth, limit: limit };
  }

  return { eligible: blockWidth <= limit, blockWidth: blockWidth, limit: limit };
}

export default {
  /* exported because FTSTableEdit pins the row-action cell to exactly this width — the
     offsets assume it, so the two must not drift apart */
  ROW_ACTION_WIDTH: ROW_ACTION_WIDTH,
  /* exported for the unit tests only; the components reach it through buildFrozenColumns */
  MAX_FROZEN_COLUMN_WIDTH: MAX_FROZEN_COLUMN_WIDTH,
  pinOverlayRequired: pinOverlayRequired,
  columnWidthFor: columnWidthFor,
  resolveFrozenColumnWidth: resolveFrozenColumnWidth,
  resolveFrozenIndices: resolveFrozenIndices,
  frozenSummary: frozenSummary,
  toggleFrozenFieldName: toggleFrozenFieldName,
  buildFrozenColumns: buildFrozenColumns,
  frozenBlockWidth: frozenBlockWidth,
  freezeEligibility: freezeEligibility
}
